import React, { useRef, useState } from "react";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AttachmentPreviewModal from "../../mail/components/AttachmentPreviewModal";
import { isPreviewable } from "../../../component/common/AttachmentPreviewList";
import ActionButton from "../../../component/common/ActionButton";
import { formatFileSize } from "../../../util/numberFormat";
import { workRequestApi } from "../api/workRequestApi";
import { ds } from "../../../styles/designSystem";

const C = ds.colors;

/** 확장자(소문자, 점 제외). 백엔드 WorkFileSupport.ext 와 같은 규칙. */
export const extOf = (filename) => {
    if (!filename) return "";
    const i = filename.lastIndexOf(".");
    return i >= 0 && i < filename.length - 1 ? filename.slice(i + 1).toLowerCase() : "";
};

/**
 * 양식 체크리스트 업로드 — <b>이 기능의 핵심 화면</b>.
 *
 * <p>기존엔 요청자가 뭘 내야 하는지 몰라 빠뜨리고, 담당자가 "○○도 보내주세요" 를 다시 메일로 물었다.
 * 여기서는 유형에 등록된 <b>양식마다 한 칸</b>이 생겨서, 각 칸을 채워야만 요청을 보낼 수 있다.
 * 내려받기와 올리기가 같은 줄에 있어 "받아서 → 채워서 → 그 자리에 올린다" 가 눈으로 이어진다.
 *
 * <p><b>파일명은 자유, 형식은 고정</b> — 요청자가 파일명을 어떻게 짓든 상관없지만 xlsx 자리에는
 * xlsx 만 올라간다(서버도 같은 규칙으로 거부하므로 화면에서 먼저 잡아 헛수고를 막는다).
 *
 * @param {Array}    formats  요청 유형의 양식 파일 목록
 * @param {object}   value    { [formatId]: File } — 채워진 칸
 * @param {Function} onChange 다음 value 를 넘겨준다
 */
export default function FormatChecklistUpload({ formats = [], value = {}, onChange, sx }) {
    const [preview, setPreview] = useState(null);
    const [errors, setErrors] = useState({});     // { [formatId]: 메시지 }

    const filledCount = formats.filter(f => value[f.id]).length;
    const allFilled = filledCount === formats.length;

    const pick = (format, file) => {
        if (!file) return;
        const want = extOf(format.originalFileName);
        const got = extOf(file.name);
        if (want && want !== got) {
            setErrors(e => ({
                ...e,
                [format.id]: `.${want} 파일을 올려주세요${got ? ` (선택한 파일: .${got})` : ""}`,
            }));
            return;
        }
        setErrors(e => ({ ...e, [format.id]: null }));
        onChange({ ...value, [format.id]: file });
    };

    const clear = (formatId) => {
        const next = { ...value };
        delete next[formatId];
        setErrors(e => ({ ...e, [formatId]: null }));
        onChange(next);
    };

    return (
        <Box sx={sx}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.textPrimary }}>
                    요청 문서
                </Typography>
                <Box sx={{
                    px: 0.9, py: 0.15, borderRadius: 5,
                    bgcolor: allFilled ? "#f0fdf4" : C.primaryBg,
                    border: `1px solid ${allFilled ? "#bbf7d0" : C.primaryBorder}`,
                }}>
                    <Typography sx={{
                        fontSize: 11, fontWeight: 700,
                        color: allFilled ? "#15803d" : C.primaryText,
                    }}>
                        {filledCount} / {formats.length}
                    </Typography>
                </Box>
            </Stack>

            <Stack spacing={1.25}>
                {formats.map((f) => (
                    <ChecklistRow
                        key={f.id}
                        format={f}
                        file={value[f.id]}
                        error={errors[f.id]}
                        onPick={(file) => pick(f, file)}
                        onClear={() => clear(f.id)}
                        onPreview={() => setPreview({
                            attachment: { id: f.id, filename: f.originalFileName, mimeType: f.contentType },
                            loader: () => workRequestApi.previewFormat(f.id),
                        })}
                    />
                ))}
            </Stack>

            {preview && (
                <AttachmentPreviewModal
                    open
                    attachment={preview.attachment}
                    loader={preview.loader}
                    onClose={() => setPreview(null)}
                />
            )}
        </Box>
    );
}

/**
 * 체크리스트 한 줄 — 양식 하나 = 제출 서류 한 칸.
 *
 * <p><b>한 줄로 압축한 이유</b>: 서류가 8개면 카드가 2행씩 쌓여 화면을 넘어가고, 그러면
 * "내가 뭘 더 내야 하는지" 를 한눈에 못 본다. 체크리스트의 핵심은 남은 항목이 보이는 것이라
 * 정보는 왼쪽, 액션은 오른쪽으로 눕혀 높이를 절반으로 줄였다.
 *
 * <p><b>카드 전체가 클릭 영역</b>이다(아직 안 올린 칸). 작은 버튼을 정확히 겨냥할 필요 없이
 * 줄 아무 데나 누르면 파일 선택창이 열린다. 미리보기·다운로드 아이콘은 stopPropagation 으로
 * 카드 클릭과 분리한다(누르면 파일 선택창이 같이 뜨는 사고 방지).
 */
function ChecklistRow({ format, file, error, onPick, onClear, onPreview }) {
    const inputRef = useRef(null);
    const want = extOf(format.originalFileName);
    const done = Boolean(file);

    const openPicker = () => inputRef.current?.click();
    /** 아이콘 버튼용 — 카드 클릭(파일 선택창)까지 같이 발동하지 않게 막는다. */
    const stop = (fn) => (e) => { e.stopPropagation(); fn?.(e); };

    return (
        <Box
            onClick={done ? undefined : openPicker}
            sx={{
                px: 1.25, py: 1,
                borderRadius: 1.5,
                border: `1px solid ${error ? "#fecaca" : done ? "#bbf7d0" : C.border}`,
                bgcolor: error ? "#fef2f2" : done ? "#f0fdf4" : "#fff",
                cursor: done ? "default" : "pointer",
                transition: "background .15s, border-color .15s",
                ...(done ? {} : { "&:hover": { bgcolor: C.primaryBg, borderColor: C.primaryBorder } }),
            }}
        >
            {/* 업로드 가능한 형식만 선택창에 보이게 한다 — 잘못 고른 뒤 거절당하는 헛수고를 없앤다.
                (서버도 같은 규칙으로 재검증하므로 accept 를 우회해도 통과되지 않는다) */}
            <input
                ref={inputRef}
                type="file"
                hidden
                accept={want ? `.${want}` : undefined}
                onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ""; }}
            />

            <Stack direction="row" alignItems="center" spacing={1.25}>
                {done
                    ? <CheckCircleRoundedIcon sx={{ fontSize: 19, color: "#16a34a", flexShrink: 0 }} />
                    : <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 19, color: C.textMuted, flexShrink: 0 }} />}
                <DescriptionRoundedIcon sx={{ fontSize: 18, color: C.primary, flexShrink: 0 }} />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600, color: C.textPrimary }}>
                        {format.originalFileName}
                    </Typography>
                    {/* 2행이 아니라 같은 칸의 보조 텍스트 — 상태(안내/올린 파일/오류)만 바뀐다. */}
                    <Typography noWrap sx={{
                        fontSize: 11,
                        color: error ? "#b91c1c" : done ? "#15803d" : C.textMuted,
                        fontWeight: done && !error ? 600 : 400,
                    }}>
                        {error
                            ? error
                            : done
                                ? `올림: ${file.name} · ${formatFileSize(file.size)}`
                                : `양식 ${formatFileSize(format.fileSize)}${want ? ` · .${want} 로 제출` : ""} · 파일명 자유`}
                    </Typography>
                </Box>

                {/* 액션 — 오른쪽으로 눕혀 높이를 먹지 않게 */}
                {done ? (
                    <>
                        <ActionButton variant="secondary" size="small" label="변경"
                                      startIcon={false} onClick={stop(openPicker)} />
                        <Tooltip title="선택 취소">
                            <IconButton size="small" onClick={stop(onClear)} sx={{ p: 0.3 }}>
                                <CloseRoundedIcon sx={{ fontSize: 16, color: C.textMuted }} />
                            </IconButton>
                        </Tooltip>
                    </>
                ) : (
                    <ActionButton variant="upload" size="small" label="작성본 올리기"
                                  onClick={stop(openPicker)} />
                )}

                {isPreviewable(format) && (
                    <Tooltip title="양식 미리보기">
                        <IconButton size="small" onClick={stop(onPreview)} sx={{ p: 0.3 }}>
                            <ZoomInRoundedIcon sx={{ fontSize: 16, color: C.textSecondary }} />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title="양식 다운로드">
                    {/* downloadUrl = Content-Disposition:attachment 가 박힌 presigned URL.
                        fileUrl 을 쓰면 S3 가 cross-origin 이라 다운로드 대신 새 탭이 열린다. */}
                    <IconButton size="small" component="a" href={format.downloadUrl || format.fileUrl}
                                onClick={stop()} sx={{ p: 0.3, color: C.primary }}>
                        <DownloadRoundedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                </Tooltip>
            </Stack>
        </Box>
    );
}
